import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import FhirDateTime from '@lifeomic/phc-web-toolkit/src/util/fhirResourceParsers/FhirDateTime';
import { memberDetailMessages } from './messages';
import { useDataset } from '../../../../util/api';
import { ExtractedResourceProvenance } from './ExtractedResourceProvenance';

const conditionFhirService = new FhirService('Condition');
const dateTimeParser = new FhirDateTime();

const useStyles = makeStyles(theme => ({
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
  },
  codeCell: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  modalSection: {
    marginBottom: 24,
  },
  modalSectionTitle: {
    marginBottom: 12,
    fontSize: '1rem',
    fontWeight: 600,
  },
  modalField: {
    marginBottom: 12,
  },
  modalLabel: {
    display: 'block',
    marginBottom: 4,
    fontWeight: 600,
  },
  modalValue: {
    display: 'block',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: 1.5,
  },
}));

const getConditionCodeDisplay = (resource: fhir.Condition): string => {
  const code = resource.code;
  if (code?.coding?.[0]?.display) return code.coding[0].display;
  if (code?.text) return code.text;
  // Elation/DSTU3 often put the problem name in note[0].text (e.g. "ADHD")
  const noteText = (resource as { note?: Array<{ text?: string }> }).note?.[0]
    ?.text;
  if (noteText) return noteText;
  // Fall back to first coding's display from any position
  const codingWithDisplay = code?.coding?.find(c => c.display);
  if (codingWithDisplay?.display) return codingWithDisplay.display;
  // Last resort: show first coding's code (e.g. "F90.9") so something is visible
  if (code?.coding?.[0]?.code) return code.coding[0].code;
  return '—';
};

const getClinicalStatusDisplay = (resource: fhir.Condition): string => {
  return resource.clinicalStatus || '—';
};

const getVerificationStatusDisplay = (resource: fhir.Condition): string => {
  return resource.verificationStatus || '—';
};

const getOnsetDisplay = (resource: fhir.Condition): string => {
  if (resource.onsetDateTime) {
    return dateTimeParser.getDisplayValue(resource.onsetDateTime) || '—';
  }
  if (resource.onsetPeriod?.start) {
    return dateTimeParser.getDisplayValue(resource.onsetPeriod.start) || '—';
  }
  if (resource.onsetString) {
    return resource.onsetString;
  }
  return '—';
};

const getCategoryDisplay = (resource: fhir.Condition): string =>
  resource.category?.[0]?.coding?.[0]?.display ||
  resource.category?.[0]?.coding?.[0]?.code ||
  resource.category?.[0]?.text ||
  '—';

type ConditionsRow = {
  id: string;
  resource: fhir.Condition;
  code: string;
  clinicalStatus: string;
  verificationStatus: string;
  onset: string;
  category: string;
};

type ConditionViewModalProps = {
  resource: fhir.Condition;
  subjectId: string;
  onDismiss: () => void;
  onRejected: (resourceId: string) => void;
};

const ConditionViewModal: React.FC<ConditionViewModalProps> = ({
  resource,
  subjectId,
  onDismiss,
  onRejected,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const codeDisplay = getConditionCodeDisplay(resource);
  const clinicalStatus = getClinicalStatusDisplay(resource);
  const verificationStatus = getVerificationStatusDisplay(resource);
  const onset = getOnsetDisplay(resource);
  const category = getCategoryDisplay(resource);

  return (
    <Modal
      isOpen={true}
      size={1}
      fullWidth
      onDismiss={onDismiss}
      title={codeDisplay}
      data-testid="condition-view-modal"
    >
      <Box padding={2} direction="column">
        <div className={classes.modalSection}>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.conditionsCode)}
            </span>
            <span className={classes.modalValue}>{codeDisplay}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.conditionsClinicalStatus)}
            </span>
            <span className={classes.modalValue}>{clinicalStatus}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.conditionsVerificationStatus)}
            </span>
            <span className={classes.modalValue}>{verificationStatus}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.conditionsOnset)}
            </span>
            <span className={classes.modalValue}>{onset}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.conditionsCategory)}
            </span>
            <span className={classes.modalValue}>{category}</span>
          </div>
        </div>
        <ExtractedResourceProvenance
          resource={resource}
          resourceType="Condition"
          subjectId={subjectId}
          onRejected={onRejected}
        />
      </Box>
    </Modal>
  );
};

export type ConditionsContentProps = {
  memberId: string;
  fallback: React.ReactNode;
};

export const ConditionsContent: React.FC<ConditionsContentProps> = ({
  memberId,
  fallback,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const accountId = useActiveAccountId();
  const auth = useServiceAuth();
  const dataset = useDataset();
  const [fetchConditions, fetchState] = useService(conditionFhirService.get);
  const [conditions, setConditions] = useState<fhir.Condition[]>([]);
  const [selectedCondition, setSelectedCondition] =
    useState<fhir.Condition | null>(null);

  const loadConditions = useCallback(async () => {
    if (!accountId || !memberId || !auth) return;
    try {
      const response = await fetchConditions(accountId, {
        subject: `Patient/${memberId}`,
        _tag: [dataset.codingToken],
        _count: 200,
      });
      const entry = response?.data?.entry ?? [];
      const resources = entry
        .map((e: { resource?: fhir.Condition }) => e.resource)
        .filter(Boolean) as fhir.Condition[];
      const sortedResources = resources.sort((a, b) => {
        const dateA = getResourceOnsetTime(a);
        const dateB = getResourceOnsetTime(b);
        return dateB - dateA;
      });
      setConditions(sortedResources);
    } catch {
      setConditions([]);
    }
  }, [accountId, memberId, auth, fetchConditions, dataset.codingToken]);

  useEffect(() => {
    void loadConditions();
  }, [loadConditions]);

  const rows: ConditionsRow[] = conditions.map(resource => ({
    id: resource.id ?? '',
    resource,
    code: getConditionCodeDisplay(resource),
    clinicalStatus: getClinicalStatusDisplay(resource),
    verificationStatus: getVerificationStatusDisplay(resource),
    onset: getOnsetDisplay(resource),
    category: getCategoryDisplay(resource),
  }));

  const columns = [
    {
      header: {
        label: formatMessage(memberDetailMessages.conditionsCode),
      },
      cell: {
        content: (row: ConditionsRow) => row.code,
        className: classes.codeCell,
      },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.conditionsClinicalStatus),
      },
      cell: { content: (row: ConditionsRow) => row.clinicalStatus },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.conditionsVerificationStatus),
      },
      cell: { content: (row: ConditionsRow) => row.verificationStatus },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.conditionsOnset),
      },
      cell: { content: (row: ConditionsRow) => row.onset },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.conditionsCategory),
      },
      cell: { content: (row: ConditionsRow) => row.category },
    },
  ];

  const handleRowClick = (row: ConditionsRow) => {
    setSelectedCondition(row.resource);
  };

  if (fetchState.isFetching && conditions.length === 0) {
    return <>{fallback}</>;
  }

  return (
    <div className={classes.tabPanel}>
      <TableModule
        isLoading={fetchState.isFetching}
        config={columns}
        data={rows}
        noResultsMessage={formatMessage(memberDetailMessages.conditionsEmpty)}
        onRowClick={handleRowClick}
        rowClickLabel={formatMessage(pwtSharedMessages.viewDetails)}
        rowRole="link"
      />
      {selectedCondition && (
        <ConditionViewModal
          resource={selectedCondition}
          subjectId={memberId}
          onDismiss={() => setSelectedCondition(null)}
          onRejected={resourceId => {
            setConditions(current =>
              current.filter(resource => resource.id !== resourceId),
            );
            setSelectedCondition(null);
          }}
        />
      )}
    </div>
  );
};

function getResourceOnsetTime(resource: fhir.Condition): number {
  if (resource.onsetDateTime) {
    return new Date(resource.onsetDateTime).getTime();
  }
  if (resource.onsetPeriod?.start) {
    return new Date(resource.onsetPeriod.start).getTime();
  }
  return 0;
}
